import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { AcknowledgeModal, ConfirmationModal, Modal } from '@percent/lemonade'
import { useMutation } from '@percent/utility'

interface RecalculateCandidateModalProps {
  isOpen: boolean
  programId: string
  candidateId: string | null
  refresh: () => Promise<unknown> | unknown
  onClose: VoidFunction
}

export function RecalculateCandidateModal({
  isOpen,
  programId,
  candidateId,
  refresh,
  onClose
}: RecalculateCandidateModalProps) {
  const { t } = useTranslation()
  const { grantProgramService } = useServices()

  const [modalState, setModalState] = useState<'success' | 'error' | null>(null)

  const [{ isPending }, { apiFunc: recalculateCandidate }] = useMutation(
    grantProgramService.recalculateProgramCandidate,
    async () => {
      await refresh()
      setModalState('success')
    },
    () => setModalState('error')
  )

  const handleClose = () => {
    setModalState(null)
    onClose()
  }

  const errorModal = modalState === 'error' && (
    <AcknowledgeModal
      result="negative"
      title={t('grants.programDetails.candidates.modal.recalculate.error.title')}
      description={t('grants.programDetails.candidates.modal.recalculate.error.description')}
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="recalculate-candidate-error-modal"
      buttonTestId="close-recalculate-candidate-error-modal"
    />
  )

  const successModal = modalState === 'success' && (
    <AcknowledgeModal
      result="positive"
      title={t('grants.programDetails.candidates.modal.recalculate.success.title')}
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="recalculate-candidate-success-modal"
      buttonTestId="close-recalculate-candidate-success-modal"
    />
  )

  return (
    <Modal open={isOpen} onClose={onClose}>
      {successModal || errorModal || (
        <ConfirmationModal
          title={t('grants.programDetails.candidates.actions.recalculate')}
          loading={isPending}
          primaryButtonText={t('grants.programDetails.candidates.modal.recalculate.confirm')}
          primaryBtnTestId="recalculate-program-modal-button"
          secondaryButtonText={t('button.cancel')}
          handleSubmit={() => candidateId && recalculateCandidate({ programId, candidateId })}
          handleClose={handleClose}
        >
          {t('grants.programDetails.candidates.modal.recalculate.description')}
        </ConfirmationModal>
      )}
    </Modal>
  )
}
